<div class="dialog-header">
  <div class="dialog-icon-wrapper">
    <div class="dialog-icon new">
      <i class="fa-solid fa-plus"></i>
    </div>
    <div>
      <h2 class="dialog-title">{{ data.id !== -1 ? 'Edit Production Request' : 'New Production Request' }}</h2>
      <p class="dialog-subtitle">Fill in the production request details</p>
    </div>
  </div>
  <button class="dialog-close" (click)="onCancel()">
    <i class="fa-solid fa-xmark"></i>
  </button>
</div>

<mat-dialog-content class="dialog-body">
  <form [formGroup]="requestForm" class="dialog-form-grid">
    <!-- Name -->
    <div class="dialog-field-group">
      <mat-label class="mat-form-field-label">Name <span class="req">*</span></mat-label>
      <mat-form-field appearance="outline">
        <input matInput formControlName="name" placeholder="Enter name">
        @if( requestForm.get('name')?.hasError('required') ) {
          <mat-error>Name is required</mat-error>
        }
        @if( requestForm.get('name')?.hasError('invalidcharacters') ) {
          <mat-error>Name has invalid characters</mat-error>
        }
      </mat-form-field>
    </div>

    <!-- Client -->
    <div class="dialog-field-group">
      <mat-label class="mat-form-field-label">Client <span class="req">*</span></mat-label>
      <mat-form-field appearance="outline">
        <input
          type="text"
          matInput
          formControlName="client"
          placeholder="Type to search client"
          [matAutocomplete]="acClient">
        <mat-autocomplete #acClient="matAutocomplete">
          @for( client of filteredClients | async; track client ) {
            <mat-option [value]="client">{{ client }}</mat-option>
          }
        </mat-autocomplete>
        @if( requestForm.get('client')?.hasError('required') ) {
          <mat-error>Client is required</mat-error>
        }
        @if( requestForm.get('client')?.hasError('invalidcharacters') ) {
          <mat-error>Client has invalid characters</mat-error>
        }
      </mat-form-field>
    </div>

    <!-- Air Date -->
    <div class="dialog-field-group">
      <mat-label class="mat-form-field-label">Air Date <span class="req">*</span></mat-label>
      <mat-form-field appearance="outline">
        <input matInput formControlName="airDate" [matDatepicker]="airDatePicker" placeholder="Select air date" readonly>
        <mat-datepicker-toggle matIconSuffix [for]="airDatePicker"></mat-datepicker-toggle>
        <mat-datepicker #airDatePicker></mat-datepicker>
      </mat-form-field>
    </div>

    <!-- Default Version Type -->
    <div class="dialog-field-group">
      <mat-label class="mat-form-field-label">Default Version Type <span class="req">*</span></mat-label>
      <mat-form-field appearance="outline">
        <mat-select formControlName="defaultVersionType" placeholder="Select version type" [compareWith]="comparatorService.compareById">
          @for( versionType of versionTypes; track versionType ) {
            <mat-option [value]="versionType">{{ versionType | objectdesc }}</mat-option>
          }
        </mat-select>
        @if( requestForm.get('defaultVersionType')?.hasError('required') ) {
          <mat-error>Default version type is required</mat-error>
        }
      </mat-form-field>
    </div>

    <!-- Require Voiceover -->
    <div class="dialog-field-group" style="display: flex; align-items: center; margin-bottom: 16px;">
      <mat-slide-toggle formControlName="requireVoiceover" (change)="onRequireVoiceoverChange($event.checked)">Require Voiceover</mat-slide-toggle>
    </div>

    <!-- Require Sample -->
    <div class="dialog-field-group" style="display: flex; align-items: center; margin-bottom: 16px;">
      <mat-slide-toggle formControlName="requireSample">Require Sample</mat-slide-toggle>
    </div>

    <!-- Voiceover Details -->
    <div class="dialog-field-group">
      <mat-label class="mat-form-field-label">Voiceover Details @if( requestForm.get('requireVoiceover')?.value ) {<span class="req">*</span>}</mat-label>
      <mat-form-field appearance="outline">
        <textarea matInput formControlName="voiceoverDetails" placeholder="Enter voiceover details" rows="5"></textarea>
        @if( requestForm.get('voiceoverDetails')?.hasError('required') ) {
          <mat-error>Voiceover details is required</mat-error>
        }
        @if( requestForm.get('voiceoverDetails')?.hasError('invalidcharacters') ) {
          <mat-error>Voiceover details has invalid characters</mat-error>
        }
      </mat-form-field>
    </div>

    <!-- Origin -->
    <div class="dialog-field-group">
      <mat-label class="mat-form-field-label">Origin <span class="req">*</span></mat-label>
      <mat-form-field appearance="outline">
        <textarea matInput formControlName="origin" placeholder="Enter origin" rows="5"></textarea>
        @if( requestForm.get('origin')?.hasError('required') ) {
          <mat-error>Origin is required</mat-error>
        }
        @if( requestForm.get('origin')?.hasError('invalidcharacters') ) {
          <mat-error>Orign has invalid characters</mat-error>
        }
      </mat-form-field>
    </div>

    <!-- Shows (Autocomplete with chips) -->
    <div class="dialog-field-group dialog-form-grid-full">
      <mat-label class="mat-form-field-label">Shows <span class="req">*</span></mat-label>
      <mat-form-field appearance="outline">
        <mat-chip-grid #chipGrid aria-label="Show selection">
          @for (show of selectedShows; track show) {
            <mat-chip-row (removed)="removeShow(show)">
              {{ show }}
              <button matChipRemove>
                <i class="fa-solid fa-xmark"></i>
              </button>
            </mat-chip-row>
          }
        </mat-chip-grid>
        <input
          #showInput
          placeholder="Type to search shows"
          formControlName="showInput"
          [matChipInputFor]="chipGrid"
          [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
          (matChipInputTokenEnd)="addShowFromInput($event)"
          [matAutocomplete]="autoShows">
        <mat-autocomplete #autoShows="matAutocomplete" (optionSelected)="addShow($event)">
          @for( show of filteredShows | async; track show ) {
            <mat-option [value]="show">{{ show }}</mat-option>
          }
        </mat-autocomplete>
        @if( selectedShows.length === 0 ) {
          <mat-error>At least one show is required</mat-error>
        }
      </mat-form-field>
    </div>

    <!-- Notes -->
    <div class="dialog-field-group">
      <mat-label class="mat-form-field-label">Notes</mat-label>
      <mat-form-field appearance="outline">
        <input matInput formControlName="notes" placeholder="Enter notes">
      </mat-form-field>
    </div>

    <!-- Master Commercial -->
    <div class="dialog-field-group">
      <mat-label class="mat-form-field-label">Master Commercial</mat-label>
      <mat-form-field appearance="outline">
        <input matInput formControlName="masterCommercial" placeholder="Master commercial reference">
        <button matIconSuffix mat-icon-button type="button" (click)="onLinkMasterCommercial()">
          <i class="fa-solid fa-magnifying-glass"></i>
        </button>
      </mat-form-field>
    </div>
  </form>
</mat-dialog-content>

<mat-dialog-actions class="dialog-footer">
  <button class="btn-ghost" (click)="onCancel()">Cancel</button>
  <button class="btn-blue" (click)="onSubmit()" [disabled]="!requestForm.valid || selectedShows.length === 0">
    {{ data.id !== -1 ? 'Update Request' : 'Create Request' }}
  </button>
</mat-dialog-actions>
